import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Sun, Moon, Bell, Mail, Loader2, ArrowLeft, Palette, Sparkles, Monitor, Users, Share2, Check, X, Shield, Download, Upload, Database, Trash2, Folder, HelpCircle, ChevronDown, FileJson, Copy, Send, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { theme, setTheme, visualTheme, setVisualTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    email_alerts: true,
    alert_email: "",
    theme: "dark",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Admin-only state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sharingWith, setSharingWith] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  // Email notification state
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingDigest, setTestingDigest] = useState(false);
  
  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTargetUser, setShareTargetUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [shareIncludeUngrouped, setShareIncludeUngrouped] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
        if (response.data.theme) {
          setTheme(response.data.theme);
        }
        
        // Check SMTP status
        const smtpResponse = await axios.get(`${API}/settings/smtp-status`);
        setSmtpConfigured(smtpResponse.data.configured);
      } catch (error) {
        console.error("Failed to fetch settings:", error);
      }
      setLoading(false);
    };

    fetchSettings();
  }, [setTheme]);

  // Fetch users for admin
  useEffect(() => {
    const fetchUsers = async () => {
      if (!user?.is_admin) return;
      
      setLoadingUsers(true);
      try {
        const response = await axios.get(`${API}/admin/users`);
        setUsers(response.data);
      } catch (error) {
        console.error("Failed to fetch users:", error);
      }
      setLoadingUsers(false);
    };

    fetchUsers();
  }, [user?.is_admin]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings`, settings);
      setSettings(response.data);
      toast.success("Settings saved");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  // Fetch groups for share dialog
  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data);
      // Select all groups by default
      setSelectedGroups(response.data.map(g => g.id));
    } catch (error) {
      console.error("Failed to fetch groups:", error);
    }
    setLoadingGroups(false);
  };

  const openShareDialog = (targetUser) => {
    setShareTargetUser(targetUser);
    setShareIncludeUngrouped(true);
    fetchGroups();
    setShareDialogOpen(true);
  };

  const handleShareData = async () => {
    if (!shareTargetUser) return;
    
    setSharingWith(shareTargetUser.id);
    try {
      const response = await axios.post(`${API}/admin/share-data`, {
        target_user_id: shareTargetUser.id,
        group_ids: selectedGroups,
        include_ungrouped: shareIncludeUngrouped
      });
      toast.success(`Data shared: ${response.data.servers_copied} servers, ${response.data.groups_copied} groups`);
      
      // Refresh users list
      const usersResponse = await axios.get(`${API}/admin/users`);
      setUsers(usersResponse.data);
      setShareDialogOpen(false);
    } catch (error) {
      console.error("Failed to share data:", error);
      toast.error(error.response?.data?.detail || "Failed to share data");
    }
    setSharingWith(null);
  };

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const selectAllGroups = () => {
    setSelectedGroups(groups.map(g => g.id));
  };

  const deselectAllGroups = () => {
    setSelectedGroups([]);
  };

  const [revokingUser, setRevokingUser] = useState(null);

  const handleRevokeShare = async (targetUserId) => {
    setRevokingUser(targetUserId);
    try {
      const response = await axios.delete(`${API}/admin/share-data/${targetUserId}`);
      toast.success(`Share revoked: ${response.data.servers_deleted} servers and ${response.data.groups_deleted} groups removed`);
      
      // Refresh users list
      const usersResponse = await axios.get(`${API}/admin/users`);
      setUsers(usersResponse.data);
    } catch (error) {
      console.error("Failed to revoke share:", error);
      toast.error(error.response?.data?.detail || "Failed to revoke share");
    }
    setRevokingUser(null);
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/data/export`);
      const data = response.data;
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `netset_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.data.servers?.length || 0} servers and ${data.data.groups?.length || 0} groups`);
    } catch (error) {
      console.error("Failed to export data:", error);
      toast.error(error.response?.data?.detail || "Failed to export data");
    }
    setExporting(false);
  };

  const handleImportData = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate the data structure
      if (!data.servers && !data.groups) {
        throw new Error("Invalid backup file format");
      }

      const response = await axios.post(`${API}/data/import`, data);
      toast.success(`Imported ${response.data.servers_imported} servers and ${response.data.groups_imported} groups`);
      
      // Refresh the page to show new data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Failed to import data:", error);
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON file");
      } else {
        toast.error(error.response?.data?.detail || error.message || "Failed to import data");
      }
    }
    setImporting(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClearData = async () => {
    setClearing(true);
    try {
      await axios.delete(`${API}/data/clear`);
      toast.success("All data cleared successfully");
      
      // Refresh the page
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Failed to clear data:", error);
      toast.error(error.response?.data?.detail || "Failed to clear data");
    }
    setClearing(false);
  };

  const copyTemplateToClipboard = () => {
    const template = {
      servers: [
        {
          name: "Example Server",
          addresses: [
            { url: "https://example.com", label: "Main Site" },
            { url: "https://api.example.com", label: "API" }
          ],
          group_id: null,
          favicon_url: null,
          is_favorite: false,
          notes: "Optional notes",
          sort_order: 0
        }
      ],
      groups: [
        {
          name: "Production",
          color: "#22c55e",
          icon: "folder",
          description: "Production servers",
          order: 0
        }
      ]
    };
    navigator.clipboard.writeText(JSON.stringify(template, null, 2));
    toast.success("Template copied to clipboard");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  // Import template for help section
  const importTemplate = `{
  "servers": [
    {
      "name": "My Server",
      "addresses": [
        { "url": "https://example.com", "label": "Main" },
        { "url": "https://api.example.com", "label": "API" }
      ],
      "group_id": null,
      "is_favorite": false,
      "notes": "Optional notes"
    }
  ],
  "groups": [
    {
      "name": "Production",
      "color": "#22c55e",
      "description": "Production servers"
    }
  ]
}`;

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="gap-2"
          data-testid="back-to-servers-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Servers
        </Button>
      </div>

      <div className="space-y-2 mb-8">
        <h1 className="text-3xl font-bold font-display">Settings</h1>
        <p className="text-muted-foreground">
          {user?.is_admin ? "Customize your dashboard" : "Manage your data"}
        </p>
      </div>

      <div className="space-y-8">
        {/* Data Import/Export Section - Available to ALL users */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Data Backup</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Export your data for backup, import from a previous backup, or clear all data
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Export Button */}
            <Button
              variant="outline"
              onClick={handleExportData}
              disabled={exporting}
              className="gap-2 h-12 justify-start"
              data-testid="export-data-btn"
            >
              {exporting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <div className="text-left">
                <div className="font-medium">Export</div>
                <div className="text-xs text-muted-foreground">Download backup</div>
              </div>
            </Button>

            {/* Import Button */}
            <div className="relative group">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportData}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                data-testid="import-data-input"
              />
              <Button
                variant="outline"
                disabled={importing}
                className="gap-2 h-12 justify-start w-full pointer-events-none group-hover:bg-accent group-hover:text-accent-foreground transition-colors"
                data-testid="import-data-btn"
              >
                {importing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Upload className="w-5 h-5" />
                )}
                <div className="text-left">
                  <div className="font-medium">Import</div>
                  <div className="text-xs text-muted-foreground group-hover:text-accent-foreground/70">Restore backup</div>
                </div>
              </Button>
            </div>

            {/* Clear Data Button */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  disabled={clearing}
                  className="gap-2 h-12 justify-start text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                  data-testid="clear-data-btn"
                >
                  {clearing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                  <div className="text-left">
                    <div className="font-medium">Clear Data</div>
                    <div className="text-xs opacity-70">Delete all servers</div>
                  </div>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all your servers and groups. This action cannot be undone.
                    We recommend exporting your data first.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleClearData}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Yes, delete everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {/* Import Help Section */}
          <Collapsible open={helpOpen} onOpenChange={setHelpOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground">
                <HelpCircle className="w-4 h-4" />
                Import Template Help
                <ChevronDown className={`w-4 h-4 transition-transform ${helpOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3">
              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <FileJson className="w-4 h-4" />
                    JSON Import Format
                  </div>
                  <Button variant="outline" size="sm" onClick={copyTemplateToClipboard} className="gap-1.5">
                    <Copy className="w-3.5 h-3.5" />
                    Copy Template
                  </Button>
                </div>
                <pre className="text-xs bg-background rounded p-3 overflow-x-auto border">
                  {importTemplate}
                </pre>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p><strong>Notes:</strong></p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li><code className="bg-muted px-1 rounded">addresses</code> is an array - each server can have multiple URLs</li>
                    <li><code className="bg-muted px-1 rounded">group_id</code> links servers to groups (use <code className="bg-muted px-1 rounded">null</code> for ungrouped)</li>
                    <li><code className="bg-muted px-1 rounded">color</code> should be a hex color code (e.g., #22c55e)</li>
                    <li>All IDs are auto-generated on import</li>
                    <li>Importing adds to existing data (doesn't replace)</li>
                  </ul>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Email Notifications Section - Available to ALL users */}
        <div className="space-y-4 pt-6 border-t">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Email Notifications</h2>
          </div>

          {!smtpConfigured ? (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">SMTP Not Configured</p>
                <p className="text-xs text-muted-foreground">
                  Email notifications require SMTP settings in the server's .env file.
                  Contact your administrator to enable this feature.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Email Alerts</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when servers go offline or recover
                  </p>
                </div>
                <Switch
                  checked={settings.email_alerts_enabled || false}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, email_alerts_enabled: checked }))
                  }
                  data-testid="email-alerts-switch"
                />
              </div>

              {settings.email_alerts_enabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="alert_email">Alert Email</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="alert_email"
                          type="email"
                          placeholder={user?.email || "alerts@example.com"}
                          value={settings.alert_email || ""}
                          onChange={(e) =>
                            setSettings((prev) => ({ ...prev, alert_email: e.target.value }))
                          }
                          className="pl-9"
                          data-testid="alert-email-input"
                        />
                      </div>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          setTestingEmail(true);
                          try {
                            await axios.post(`${API}/settings/test-email`);
                            toast.success("Test email sent!");
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Failed to send test email");
                          }
                          setTestingEmail(false);
                        }}
                        disabled={testingEmail}
                        className="gap-2"
                        data-testid="test-email-btn"
                      >
                        {testingEmail ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Test
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Leave empty to use your login email ({user?.email})
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div className="space-y-0.5">
                        <Label className="text-sm">Server Down</Label>
                        <p className="text-xs text-muted-foreground">
                          Alert when server goes offline
                        </p>
                      </div>
                      <Switch
                        checked={settings.notify_on_down !== false}
                        onCheckedChange={(checked) =>
                          setSettings((prev) => ({ ...prev, notify_on_down: checked }))
                        }
                        data-testid="notify-down-switch"
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div className="space-y-0.5">
                        <Label className="text-sm">Server Recovery</Label>
                        <p className="text-xs text-muted-foreground">
                          Alert when server comes back
                        </p>
                      </div>
                      <Switch
                        checked={settings.notify_on_recovery !== false}
                        onCheckedChange={(checked) =>
                          setSettings((prev) => ({ ...prev, notify_on_recovery: checked }))
                        }
                        data-testid="notify-recovery-switch"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cooldown">Alert Cooldown (minutes)</Label>
                    <Input
                      id="cooldown"
                      type="number"
                      min="1"
                      max="60"
                      value={settings.cooldown_minutes || 5}
                      onChange={(e) =>
                        setSettings((prev) => ({ ...prev, cooldown_minutes: parseInt(e.target.value) || 5 }))
                      }
                      className="w-24"
                      data-testid="cooldown-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Minimum time between alerts for the same server
                    </p>
                  </div>

                  {/* Quiet Hours Section */}
                  <div className="space-y-4 pt-4 mt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="flex items-center gap-2">
                          <Moon className="w-4 h-4 text-muted-foreground" />
                          Quiet Hours
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Pause instant alerts and get a morning digest instead
                        </p>
                      </div>
                      <Switch
                        checked={settings.quiet_hours_enabled || false}
                        onCheckedChange={(checked) =>
                          setSettings((prev) => ({ ...prev, quiet_hours_enabled: checked }))
                        }
                        data-testid="quiet-hours-switch"
                      />
                    </div>

                    {settings.quiet_hours_enabled && (
                      <div className="space-y-4 pl-4 border-l-2 border-muted">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="quiet_from">From</Label>
                            <Input
                              id="quiet_from"
                              type="time"
                              value={settings.quiet_hours_from || "22:00"}
                              onChange={(e) =>
                                setSettings((prev) => ({ ...prev, quiet_hours_from: e.target.value }))
                              }
                              data-testid="quiet-from-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="quiet_to">To</Label>
                            <Input
                              id="quiet_to"
                              type="time"
                              value={settings.quiet_hours_to || "08:00"}
                              onChange={(e) =>
                                setSettings((prev) => ({ ...prev, quiet_hours_to: e.target.value }))
                              }
                              data-testid="quiet-to-input"
                            />
                          </div>
                        </div>

                        {/* Day Selector */}
                        <div className="space-y-2">
                          <Label>Active Days</Label>
                          <p className="text-xs text-muted-foreground mb-2">
                            Quiet hours only apply on selected days
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { key: 'mon', label: 'Mon' },
                              { key: 'tue', label: 'Tue' },
                              { key: 'wed', label: 'Wed' },
                              { key: 'thu', label: 'Thu' },
                              { key: 'fri', label: 'Fri' },
                              { key: 'sat', label: 'Sat' },
                              { key: 'sun', label: 'Sun' },
                            ].map((day) => {
                              const days = settings.quiet_hours_days || ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                              const isSelected = days.includes(day.key);
                              return (
                                <button
                                  key={day.key}
                                  type="button"
                                  onClick={() => {
                                    const currentDays = settings.quiet_hours_days || ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                                    const newDays = isSelected
                                      ? currentDays.filter(d => d !== day.key)
                                      : [...currentDays, day.key];
                                    setSettings((prev) => ({ ...prev, quiet_hours_days: newDays }));
                                  }}
                                  className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                    isSelected
                                      ? 'bg-primary text-primary-foreground border-primary'
                                      : 'bg-background border-border hover:bg-muted'
                                  }`}
                                  data-testid={`quiet-day-${day.key}`}
                                >
                                  {day.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* All day on weekends toggle */}
                        <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                          <div className="space-y-0.5">
                            <Label className="text-sm">All Day on Weekends</Label>
                            <p className="text-xs text-muted-foreground">
                              Quiet hours apply 24/7 on Saturday & Sunday
                            </p>
                          </div>
                          <Switch
                            checked={settings.weekend_all_day || false}
                            onCheckedChange={(checked) =>
                              setSettings((prev) => ({ ...prev, weekend_all_day: checked }))
                            }
                            data-testid="weekend-all-day-switch"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="timezone">Timezone</Label>
                          <select
                            id="timezone"
                            value={settings.timezone || "Europe/London"}
                            onChange={(e) =>
                              setSettings((prev) => ({ ...prev, timezone: e.target.value }))
                            }
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            data-testid="timezone-select"
                          >
                            <option value="Europe/London">London (GMT/BST)</option>
                            <option value="Europe/Paris">Paris (CET/CEST)</option>
                            <option value="Europe/Berlin">Berlin (CET/CEST)</option>
                            <option value="America/New_York">New York (EST/EDT)</option>
                            <option value="America/Los_Angeles">Los Angeles (PST/PDT)</option>
                            <option value="America/Chicago">Chicago (CST/CDT)</option>
                            <option value="Asia/Tokyo">Tokyo (JST)</option>
                            <option value="Asia/Singapore">Singapore (SGT)</option>
                            <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                            <option value="UTC">UTC</option>
                          </select>
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                          <div className="space-y-0.5">
                            <Label className="text-sm">Morning Digest</Label>
                            <p className="text-xs text-muted-foreground">
                              Email summary of overnight events + servers still down
                            </p>
                          </div>
                          <Switch
                            checked={settings.send_morning_digest !== false}
                            onCheckedChange={(checked) =>
                              setSettings((prev) => ({ ...prev, send_morning_digest: checked }))
                            }
                            data-testid="morning-digest-switch"
                          />
                        </div>

                        <p className="text-xs text-muted-foreground bg-blue-500/10 p-3 rounded-lg">
                          <strong>How it works:</strong> During quiet hours, status changes are logged but don't trigger instant emails. 
                          At {settings.quiet_hours_to || "08:00"}, you'll receive a digest showing which servers are still down 
                          and a summary of overnight activity.
                          <br /><br />
                          <strong>Your config:</strong> {settings.quiet_hours_from || "22:00"} - {settings.quiet_hours_to || "08:00"} on weekdays
                          {settings.weekend_all_day ? ", all day on weekends" : ""}.
                          <br /><br />
                          <strong>Tip:</strong> Mark servers as "Critical" (shield icon) to receive instant alerts even during quiet hours.
                        </p>

                        {/* Test Digest Button - Admin only */}
                        {user?.is_admin && (
                          <Button
                            variant="outline"
                            onClick={async () => {
                              setTestingDigest(true);
                              try {
                                await axios.post(`${API}/admin/test-digest`);
                                toast.success("Mock digest email sent!");
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Failed to send mock digest");
                              }
                              setTestingDigest(false);
                            }}
                            disabled={testingDigest}
                            className="gap-2 w-full"
                            data-testid="test-digest-btn"
                          >
                            {testingDigest ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                            Send Test Digest Email
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Save Button for non-admin users */}
        {!user?.is_admin && (
          <div className="pt-6 border-t">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="gap-2"
              data-testid="save-settings-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Settings
            </Button>
          </div>
        )}

        {/* Admin-only sections below */}
        {user?.is_admin && (
          <>
        {/* Visual Theme Section */}
        <div className="space-y-4 pt-6 border-t">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Visual Theme</h2>
          </div>
          <p className="text-sm text-muted-foreground">Choose your dashboard style</p>

          <div className="grid gap-3 sm:grid-cols-2">
            {/* Netset Original */}
            <button
              onClick={() => setVisualTheme('netset')}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                visualTheme === 'netset'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid="theme-netset-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">Netset Original</div>
                  <div className="text-sm text-muted-foreground">Clean and professional</div>
                </div>
                {visualTheme === 'netset' && (
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
              </div>
              <div className="flex gap-1.5 mt-3">
                <div className="w-6 h-6 rounded bg-rose-500" title="Primary" />
                <div className="w-6 h-6 rounded bg-pink-100" title="Light BG" />
                <div className="w-6 h-6 rounded bg-emerald-500" title="Success" />
                <div className="w-6 h-6 rounded bg-gray-200" title="Muted" />
              </div>
            </button>

            {/* Cyber Tech */}
            <button
              onClick={() => setVisualTheme('cyber')}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                visualTheme === 'cyber'
                  ? 'border-cyan-500 bg-cyan-500/5'
                  : 'border-border hover:border-cyan-500/50'
              }`}
              data-testid="theme-cyber-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">Cyber Tech</div>
                  <div className="text-sm text-muted-foreground">Futuristic neon style</div>
                </div>
                {visualTheme === 'cyber' && (
                  <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-black" />
                  </div>
                )}
              </div>
              <div className="flex gap-1.5 mt-3">
                <div className="w-6 h-6 rounded bg-cyan-500" title="Primary" />
                <div className="w-6 h-6 rounded bg-slate-900" title="Background" />
                <div className="w-6 h-6 rounded bg-green-400" title="Success" />
                <div className="w-6 h-6 rounded bg-fuchsia-500" title="Accent" />
              </div>
            </button>

            {/* Matrix */}
            <button
              onClick={() => setVisualTheme('matrix')}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                visualTheme === 'matrix'
                  ? 'border-green-500 bg-green-500/5'
                  : 'border-border hover:border-green-500/50'
              }`}
              data-testid="theme-matrix-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center border border-green-500/50">
                  <span className="text-green-500 font-mono text-lg font-bold" style={{ textShadow: '0 0 10px #00FF00' }}>01</span>
                </div>
                <div className="flex-1">
                  <div className="font-medium">Matrix</div>
                  <div className="text-sm text-muted-foreground">Digital rain terminal</div>
                </div>
                {visualTheme === 'matrix' && (
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-black" />
                  </div>
                )}
              </div>
              <div className="flex gap-1.5 mt-3">
                <div className="w-6 h-6 rounded bg-green-500" title="Matrix Green" />
                <div className="w-6 h-6 rounded bg-black border border-green-900" title="Background" />
                <div className="w-6 h-6 rounded bg-green-700" title="Dark Green" />
                <div className="w-6 h-6 rounded bg-green-400" title="Glow" />
              </div>
            </button>
          </div>
        </div>

        {/* Appearance Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Appearance</h2>
          </div>
          <p className="text-sm text-muted-foreground">Light or dark mode</p>

          <div className="flex gap-3">
            <Button
              variant={theme === 'light' ? 'default' : 'outline'}
              className="flex-1 gap-2"
              onClick={() => {
                setTheme('light');
                setSettings(prev => ({ ...prev, theme: 'light' }));
              }}
              disabled={visualTheme === 'cyber' || visualTheme === 'matrix'}
            >
              <Sun className="w-4 h-4" />
              Light
            </Button>
            <Button
              variant={theme === 'dark' ? 'default' : 'outline'}
              className="flex-1 gap-2"
              onClick={() => {
                setTheme('dark');
                setSettings(prev => ({ ...prev, theme: 'dark' }));
              }}
              disabled={visualTheme === 'cyber' || visualTheme === 'matrix'}
            >
              <Moon className="w-4 h-4" />
              Dark
            </Button>
          </div>
          {(visualTheme === 'cyber' || visualTheme === 'matrix') && (
            <p className="text-xs text-muted-foreground">
              This visual theme has a fixed appearance
            </p>
          )}
        </div>

        {/* User Management Section - Admin Only */}
        <div className="space-y-4 pt-6 border-t">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">User Management</h2>
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Admin</span>
          </div>
          <p className="text-sm text-muted-foreground">
            View all users and share your server data with them
          </p>

          {loadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No other users have signed up yet.</p>
          ) : (
            <div className="space-y-3">
              {users.map((u) => (
                <div
                  key={u.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    u.is_admin ? 'border-primary/30 bg-primary/5' : 'border-border'
                  }`}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={u.picture} alt={u.name} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {u.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{u.name}</p>
                      {u.is_admin && (
                        <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded">Admin</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {u.server_count || 0} servers • Joined {new Date(u.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {!u.is_admin && (
                    <div className="flex items-center gap-2">
                      {u.has_shared_data ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={revokingUser === u.id}
                              className="gap-1.5 text-destructive hover:text-destructive"
                              data-testid={`revoke-share-btn-${u.id}`}
                            >
                              {revokingUser === u.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <X className="w-3.5 h-3.5" />
                              )}
                              Revoke
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Revoke Data Share?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove all servers and groups from <strong>{u.name}</strong>'s account. 
                                They will no longer have access to any shared data. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRevokeShare(u.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Yes, revoke access
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openShareDialog(u)}
                          disabled={sharingWith === u.id}
                          className="gap-1.5"
                          data-testid={`share-data-btn-${u.id}`}
                        >
                          {sharingWith === u.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Share2 className="w-3.5 h-3.5" />
                          )}
                          Share Data
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Button - Admin */}
        <div className="pt-6 border-t">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-2"
            data-testid="save-settings-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Settings
          </Button>
        </div>
        </>
        )}
      </div>

      {/* Share Data Dialog - Admin only */}
      {user?.is_admin && (
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Data with {shareTargetUser?.name}
            </DialogTitle>
            <DialogDescription>
              Select which groups to share. Servers in selected groups will be copied to this user.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {loadingGroups ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Select All / Deselect All */}
                <div className="flex gap-2 pb-2 border-b">
                  <Button variant="ghost" size="sm" onClick={selectAllGroups}>
                    Select All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={deselectAllGroups}>
                    Deselect All
                  </Button>
                </div>

                {/* Ungrouped servers option */}
                <div className="flex items-center space-x-3 p-3 rounded-lg border bg-muted/30">
                  <Checkbox
                    id="ungrouped"
                    checked={shareIncludeUngrouped}
                    onCheckedChange={setShareIncludeUngrouped}
                  />
                  <label
                    htmlFor="ungrouped"
                    className="flex-1 flex items-center gap-2 text-sm font-medium cursor-pointer"
                  >
                    <Folder className="w-4 h-4 text-muted-foreground" />
                    Ungrouped Servers
                  </label>
                </div>

                {/* Groups list */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {groups.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No groups found. Only ungrouped servers will be shared.
                    </p>
                  ) : (
                    groups.map((group) => (
                      <div
                        key={group.id}
                        className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          id={group.id}
                          checked={selectedGroups.includes(group.id)}
                          onCheckedChange={() => toggleGroupSelection(group.id)}
                        />
                        <label
                          htmlFor={group.id}
                          className="flex-1 flex items-center gap-2 text-sm font-medium cursor-pointer"
                        >
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: group.color || '#3b82f6' }}
                          />
                          {group.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  {selectedGroups.length} group{selectedGroups.length !== 1 ? 's' : ''} selected
                  {shareIncludeUngrouped ? ' + ungrouped servers' : ''}
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleShareData}
              disabled={sharingWith || (!shareIncludeUngrouped && selectedGroups.length === 0)}
              className="gap-2"
            >
              {sharingWith ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Share2 className="w-4 h-4" />
              )}
              Share Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
};

export default Settings;
